/**
 * Vendor / partner onboarding API client  ("frontend-first" contract).
 *
 * These are the endpoints the website expects the backend team to implement on
 * customerapi.pulpitcabs.com. Until they exist, every call degrades gracefully
 * (result.notImplemented === true) so the wizard stays fully usable and drafts
 * are never lost. The one endpoint that already exists today is add_partner.
 *
 * Contract summary (full doc: PARTNER_ONBOARDING_API.md at repo root):
 *
 *   POST /website/partner/otp/send      { mobile_number }
 *        -> { success, request_id, ttl_seconds }
 *   POST /website/partner/otp/verify    { mobile_number, otp, request_id }
 *        -> { success, partner_id, resume_token }
 *   POST /website/add_partner           (Phase 1 lead, EXISTS today)
 *        -> { success, partner_id? }
 *   GET  /website/partner/{resume_token}
 *        -> { success, partner: {...onboarding draft} }
 *   POST /website/partner/upload-url    { partner_id, doc_key, filename, content_type }
 *        -> { success, upload_url, file_key }         (S3 pre-signed PUT)
 *   POST /website/partner/{partner_id}/onboarding   { ...full payload }
 *        -> { success, status: "under_review" }
 *
 * Every function resolves to a uniform shape:
 *   { ok: boolean, data?: any, notImplemented?: boolean, message?: string }
 * It never rejects, so callers use plain awaits without try/catch noise.
 */

import axios from "axios"

const BASE =
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_CMS_URL ||
  "https://customerapi.pulpitcabs.com"

const JSON_CFG = { headers: { "Content-Type": "application/json" }, timeout: 20000 }

// Treat "backend not built yet" (404/501) and network failures as a soft,
// recoverable condition distinct from a real 4xx/5xx business error.
const classify = (error) => {
  const status = error?.response?.status
  const notImplemented = status === 404 || status === 501 || !error?.response
  return {
    ok: false,
    notImplemented,
    message:
      error?.response?.data?.message ||
      (notImplemented ? "This step is not connected yet" : error.message) ||
      "Something went wrong",
  }
}

const ok = (data) => ({ ok: true, data })

/* ------------------------------------------------------------------ */
/*  Phase 1: lead + OTP                                                */
/* ------------------------------------------------------------------ */

/** Phase 1 lead. Hits the endpoint that already exists today. */
export const submitPartnerLead = async (payload) => {
  try {
    const res = await axios.post(`${BASE}/website/add_partner`, payload, JSON_CFG)
    return ok(res.data)
  } catch (error) {
    return classify(error)
  }
}

/** Send a 6-digit OTP to the vendor's mobile. */
export const sendPartnerOtp = async (mobile_number) => {
  try {
    const res = await axios.post(`${BASE}/website/partner/otp/send`, { mobile_number }, JSON_CFG)
    return ok(res.data)
  } catch (error) {
    return classify(error)
  }
}

/** Verify the OTP. On success the backend returns partner_id + resume_token. */
export const verifyPartnerOtp = async ({ mobile_number, otp, request_id }) => {
  try {
    const res = await axios.post(
      `${BASE}/website/partner/otp/verify`,
      { mobile_number, otp, request_id },
      JSON_CFG
    )
    return ok(res.data)
  } catch (error) {
    return classify(error)
  }
}

/* ------------------------------------------------------------------ */
/*  Phase 2: resume, uploads, final submit                            */
/* ------------------------------------------------------------------ */

/** Pull a saved server-side draft by resume token (for the resume link). */
export const getPartnerProfile = async (resume_token) => {
  try {
    const res = await axios.get(`${BASE}/website/partner/${encodeURIComponent(resume_token)}`, JSON_CFG)
    return ok(res.data)
  } catch (error) {
    return classify(error)
  }
}

/**
 * Ask the backend for a pre-signed S3 URL, then PUT the file straight to S3.
 * Returns { ok, data: { file_key } } so the wizard stores only the key.
 * No credentials ever touch the browser: the backend signs the URL.
 */
export const uploadPartnerDocument = async ({ partner_id, doc_key, file }) => {
  try {
    const signRes = await axios.post(
      `${BASE}/website/partner/upload-url`,
      { partner_id, doc_key, filename: file.name, content_type: file.type || "application/octet-stream" },
      JSON_CFG
    )
    const { upload_url, file_key } = signRes.data || {}
    if (!upload_url) return { ok: false, notImplemented: true, message: "No upload URL returned" }

    await axios.put(upload_url, file, {
      headers: { "Content-Type": file.type || "application/octet-stream" },
      timeout: 60000,
    })
    return ok({ file_key, filename: file.name, size: file.size })
  } catch (error) {
    return classify(error)
  }
}

/** Final submit of the complete onboarding payload. */
export const submitPartnerOnboarding = async ({ partner_id, payload }) => {
  try {
    const res = await axios.post(
      `${BASE}/website/partner/${encodeURIComponent(partner_id || "new")}/onboarding`,
      payload,
      { ...JSON_CFG, timeout: 30000 }
    )
    return ok(res.data)
  } catch (error) {
    return classify(error)
  }
}
